/**
 * useChartData.js
 * ─────────────────────────────────────────────────────────────────────────────
 * React hook owning all data concerns: REST fetch, chart transforms,
 * WebSocket, earnings markers, signal badges, candlestick patterns.
 *
 * BRIDGE REF UPDATE (v8 Batch 3):
 *   earnEventsRef, signalBadgesRef, candlePatternsRef now accepted as params
 *   (created by useChartRenderer, passed via PriceChart.jsx). This hook
 *   WRITES to them; useChartRenderer READS them in doRenderChart.
 *
 * Fixes from Batch 1 preserved:
 *   - apiFetch shadow for automatic 401 token refresh
 *   - Explanatory comments on all eslint-disable lines
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from 'react';
import * as d3 from 'd3';

import apiFetch from '../utils/apiFetch';

import {
  API_URL, WS_TIINGO_KEY, WS_FINNHUB_KEY, WS_PROVIDER, TABS,
} from '../constants/chartConfig';
import {
  calcRSI, calcSMA, calcATR14Single,
  calcHeikinAshi, calcRenko,
  detectSMACrossover,
} from '../lib/calcIndicators';
import { createDateIndexScale, toET } from '../lib/chartScales';
import { detectSignalBadges, detectCandlePatterns } from '../lib/calcPatterns';

// Must be after all imports — CI=true enforces import/first ESLint rule.
// Shadows global fetch so all calls in this module get automatic 401 token refresh.
const fetch = apiFetch; // eslint-disable-line no-unused-vars

export default function useChartData({
  ticker,
  activeTab,
  intraRes,
  showExtHours,
  chartType,
  renkoBrickOverride,
  activeIndicators,
  indicatorRegistry,
  // Shared refs — owned by useChartRenderer, written here after fetch
  candlesRef,
  chartDataRef,
  tradingDatesRef,
  dateToIndexRef,
  xScaleRef,
  xScaleBaseRef,
  yScaleRef,
  zoomRef,
  gRef,
  innerWRef,
  innerHRef,
  wsRef,
  wsLiveRef,
  wsReconnectRef,
  wsTickerRef,
  showExtHoursRef,
  chartTypeRef,
  // Bridge refs — created by useChartRenderer, passed via PriceChart.jsx.
  // This hook WRITES; doRenderChart in useChartRenderer READS.
  earnEventsRef,
  signalBadgesRef,
  candlePatternsRef,
  // Render callbacks
  doRenderChart,
  doRenderPanels,
  // Analytics
  logRef,
}) {

  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [lastBar,       setLastBar]       = useState(null);
  const [priceChange,   setPriceChange]   = useState(null);
  const [rsiValue,      setRsiValue]      = useState(null);
  const [rsiPrev,       setRsiPrev]       = useState(null);
  const [renkoBrickAtr, setRenkoBrickAtr] = useState(null);
  const [wsStatus,      setWsStatus]      = useState('off');

  // State drives React re-renders. Refs (bridge) hold the actual data for D3.
  const [earnEvents,    setEarnEvents]    = useState([]);
  const [signalBadges,  setSignalBadges]  = useState([]);
  const [candlePatterns,setCandlePatterns]= useState([]);

  // Write state → bridge refs, then trigger repaint
  useEffect(() => {
    earnEventsRef.current = earnEvents; doRenderChart();
  }, [earnEvents]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: doRenderChart is stable ([] useCallback). Not a dep.

  useEffect(() => {
    signalBadgesRef.current = signalBadges; doRenderChart();
  }, [signalBadges]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: same reason as earnEvents above.

  useEffect(() => {
    candlePatternsRef.current = candlePatterns; doRenderChart();
  }, [candlePatterns]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: same reason as earnEvents above.

  // ── Reset on ticker change ─────────────────────────────────────────────────
  useEffect(() => {
    setPriceChange(null); setLastBar(null);
    setRsiValue(null);   setRsiPrev(null);
    setEarnEvents([]);   setSignalBadges([]); setCandlePatterns([]);
    logRef.current?.('chart_view');
  }, [ticker]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: reset on ticker only. logRef is a stable ref, not a dep.

  // ── Historical OHLCV fetch ─────────────────────────────────────────────────
  useEffect(() => {
    if (!ticker) return;
    setLoading(true); setError(null);

    const tab        = TABS[activeTab];
    const toTs       = Math.floor(Date.now() / 1000);
    const fromTs     = toTs - tab.days * 86400;
    const resolution = activeTab === 0 ? intraRes : tab.resolution;

    fetch(`${API_URL}/api/seo/${ticker}/chart?resolution=${resolution}&from=${fromTs}&to=${toTs}`)
      .then(r => r.json())
      .then(data => {
        if (!data.candles?.length) { setError('No chart data'); return; }
        const raw = data.candles;
        candlesRef.current = raw;

        const atr14 = calcATR14Single(raw);
        setRenkoBrickAtr(atr14);

        const sessionRaw = (activeTab === 0 && !showExtHoursRef.current)
          ? raw.filter(d => {
              const et = toET(new Date(d.t * 1000));
              const h  = et.getUTCHours(), m = et.getUTCMinutes();
              return (h > 9 || (h === 9 && m >= 30)) && h < 16;
            })
          : raw;

        const getDisplay = () => {
          const ct = chartTypeRef.current;
          if (ct === 'heikinashi') return calcHeikinAshi(sessionRaw);
          if (ct === 'renko')
            return calcRenko(sessionRaw, renkoBrickOverride || atr14 || (sessionRaw[sessionRaw.length - 1]?.c * 0.01));
          return sessionRaw;
        };

        const chartData = getDisplay().map(d => ({
          date: new Date(d.t * 1000), open: d.o, high: d.h, low: d.l, close: d.c, volume: d.v || 0,
        }));
        chartDataRef.current = chartData;

        const innerW       = innerWRef.current || 600;
        const innerH       = innerHRef.current || 300;
        const tradingDates = chartData.map(d => d.date);
        const dateToIndex  = new Map(tradingDates.map((d, i) => [d.getTime(), i]));
        tradingDatesRef.current = tradingDates;
        dateToIndexRef.current  = dateToIndex;

        const xLinear = d3.scaleLinear().domain([0, tradingDates.length - 1]).range([0, innerW]);
        const xBase   = createDateIndexScale(xLinear, tradingDates, dateToIndex);
        xScaleBaseRef.current = xBase;
        xScaleRef.current     = xBase.copy();
        yScaleRef.current     = d3.scaleLinear()
          .domain([d3.min(chartData, d => d.low) * 0.99, d3.max(chartData, d => d.high) * 1.01])
          .range([innerH, 0]);

        if (gRef.current)
          d3.select(gRef.current.closest('svg')).call(zoomRef.current.transform, d3.zoomIdentity);

        const first = chartData[0], last = chartData[chartData.length - 1];
        setPriceChange({ change: last.close - first.open, pct: ((last.close - first.open) / first.open) * 100 });
        setLastBar(last);

        const rsiData = calcRSI(raw, 14);
        if (rsiData.length) { setRsiPrev(rsiValue); setRsiValue(rsiData[rsiData.length - 1].value); }

        const crossover = detectSMACrossover(calcSMA(raw, 20), calcSMA(raw, 50));
        if (crossover) logRef.current?.('sma_crossover', { crossoverType: crossover });

        doRenderChart(); doRenderPanels();
        setSignalBadges(detectSignalBadges(chartData, activeIndicators, indicatorRegistry));
        setCandlePatterns(detectCandlePatterns(chartData));
      })
      .catch(() => setError('Chart unavailable'))
      .finally(() => setLoading(false));
  }, [ticker, activeTab, intraRes, showExtHours, doRenderChart, doRenderPanels]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: showExtHours triggers re-fetch; filter uses showExtHoursRef.
  // chartType/renkoBrickOverride read via refs — excluded (display-only change).

  // ── Recompute badges when indicators change ────────────────────────────────
  useEffect(() => {
    if (chartDataRef.current.length)
      setSignalBadges(detectSignalBadges(chartDataRef.current, activeIndicators, indicatorRegistry));
  }, [activeIndicators]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: chartDataRef and indicatorRegistry are stable refs/constants.

  // ── Earnings markers ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!ticker || !WS_FINNHUB_KEY) return;
    const now  = new Date();
    const from = new Date(now.getFullYear() - 2, now.getMonth(), now.getDate()).toISOString().slice(0, 10);
    const to   = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate()).toISOString().slice(0, 10);
    fetch(`https://finnhub.io/api/v1/stock/earnings?symbol=${ticker}&from=${from}&to=${to}&token=${WS_FINNHUB_KEY}`)
      .then(r => r.json())
      .then(data => setEarnEvents((Array.isArray(data) ? data : []).map(e => ({
        date: new Date(e.date), type: 'E',
        label: e.epsActual != null ? `EPS ${e.epsActual > 0 ? '+' : ''}${e.epsActual.toFixed(2)}` : 'Earnings',
      }))))
      .catch(() => {});
  }, [ticker]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: WS_FINNHUB_KEY is a module-level constant.

  // ── Live WebSocket ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ticker || !WS_PROVIDER || activeTab !== 0) { setWsStatus('off'); return; }
    wsTickerRef.current = ticker;
    let ws;

    const handleTrade = (price, volume) => {
      const raw = candlesRef.current; if (!raw.length) return;
      const last = raw[raw.length - 1];
      last.c = price; if (price > last.h) last.h = price; if (price < last.l) last.l = price;
      last.v = (last.v || 0) + (volume || 0);
      const cd = chartDataRef.current;
      if (cd.length) {
        const lc = cd[cd.length - 1];
        lc.close = price; lc.high = Math.max(lc.high, price); lc.low = Math.min(lc.low, price);
        lc.volume = (lc.volume || 0) + (volume || 0);
      }
      setLastBar(prev => prev ? { ...prev, close: price } : null);
      setPriceChange(prev => {
        if (!prev || !chartDataRef.current.length) return prev;
        const chg = price - chartDataRef.current[0].open;
        return { change: chg, pct: (chg / chartDataRef.current[0].open) * 100 };
      });
      doRenderChart();
    };

    const connectTiingo = () => {
      setWsStatus('connecting');
      ws = new WebSocket('wss://api.tiingo.com/iex');
      ws.onopen = () => { ws.send(JSON.stringify({ eventName: 'subscribe', authorization: WS_TIINGO_KEY, eventData: { thresholdLevel: 5, tickers: [ticker] } })); wsLiveRef.current = true; setWsStatus('live'); };
      ws.onmessage = e => { try { const m = JSON.parse(e.data); if (m.messageType === 'T' && m.data?.ticker?.toUpperCase() === wsTickerRef.current?.toUpperCase()) handleTrade(m.data.p || m.data.last, m.data.s || m.data.lastSize); } catch {} };
      ws.onerror = () => { wsLiveRef.current = false; setWsStatus('error'); if (WS_FINNHUB_KEY) connectFinnhub(); };
      ws.onclose = () => { if (wsLiveRef.current) { wsLiveRef.current = false; wsReconnectRef.current = setTimeout(connectTiingo, 5000); } };
    };
    const connectFinnhub = () => {
      setWsStatus('connecting');
      ws = new WebSocket(`wss://ws.finnhub.io?token=${WS_FINNHUB_KEY}`);
      ws.onopen = () => { ws.send(JSON.stringify({ type: 'subscribe', symbol: ticker })); wsLiveRef.current = true; setWsStatus('live'); };
      ws.onmessage = e => { try { const m = JSON.parse(e.data); if (m.type === 'trade') m.data?.forEach(t => { if (t.s?.toUpperCase() === wsTickerRef.current?.toUpperCase()) handleTrade(t.p, t.v); }); } catch {} };
      ws.onerror = () => { wsLiveRef.current = false; setWsStatus('error'); };
      ws.onclose = () => { if (wsLiveRef.current) { wsLiveRef.current = false; wsReconnectRef.current = setTimeout(connectFinnhub, 5000); } };
    };

    if (WS_PROVIDER === 'tiingo') connectTiingo(); else connectFinnhub();
    wsRef.current = ws;

    return () => {
      wsLiveRef.current = false; clearTimeout(wsReconnectRef.current);
      if (ws) {
        try { ws.send(JSON.stringify(WS_PROVIDER === 'tiingo' ? { eventName: 'unsubscribe', authorization: WS_TIINGO_KEY, eventData: { tickers: [ticker] } } : { type: 'unsubscribe', symbol: ticker })); } catch {}
        ws.close();
      }
      setWsStatus('off');
    };
  }, [ticker, activeTab, doRenderChart]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: WS env keys are module constants. All WS refs are stable.

  return {
    loading, error, lastBar, priceChange,
    rsiValue, rsiPrev, renkoBrickAtr, setRenkoBrickAtr,
    earnEvents, signalBadges, candlePatterns, wsStatus,
  };
}
